import { getTenantSlugHostError } from "@repo/shared";
import { useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { WrongTenantOverlay } from "@/components/auth/wrong-tenant-overlay";
import { FullScreenLoader } from "@/components/full-screen-loader";
import { getSessionTenantSlug, useSession } from "@/lib/auth-client";
import { getTenantHostContext, tenantUrl } from "@/lib/tenant-host";

/**
 * Query params that steer the login page and must never survive on the
 * protected URL itself: `?tenant=<slug>` preselects the organization and
 * `?sso=true` triggers SSO immediately. They are stripped whether or not the
 * user is signed in; when signed out they are forwarded to /login.
 */
const LOGIN_HINT_PARAMS = ["tenant", "sso"] as const;

/**
 * Client-side auth guard for protected routes.
 * Replaces Next.js middleware auth checks in the SPA.
 *
 * - While session is loading → shows a full-screen spinner
 * - QL-343 tenant subdomains: first moves the browser to the right host, keeping
 *   the path — `?tenant=<slug>` wins, then (on the root domain) the session's
 *   tenant. A session for another tenant than this host shows an overlay.
 * - No session → redirects to /login?redirect=<current path> (plus any login hints)
 * - Has session → strips login hints from the URL and renders child routes via <Outlet />
 */
export function AuthGuard() {
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const hostContext = useMemo(getTenantHostContext, []);
  const hostSlug = hostContext.kind === "tenant" ? hostContext.slug : null;
  const sessionTenantSlug = getSessionTenantSlug(session);

  const { hints, cleanedUrl } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const hints = new URLSearchParams();
    for (const key of LOGIN_HINT_PARAMS) {
      const value = params.get(key);
      if (value !== null) hints.set(key, value);
      params.delete(key);
    }
    const cleanedSearch = params.size > 0 ? `?${params.toString()}` : "";
    return { hints, cleanedUrl: location.pathname + cleanedSearch + location.hash };
  }, [location.pathname, location.search, location.hash]);

  // Tenant host the browser has to move to before anything renders, if any
  const hostRedirectSlug = useMemo(() => {
    if (hostContext.kind !== "root" && hostContext.kind !== "tenant") return null;
    const hinted = hints.get("tenant")?.toLowerCase().trim() || null;
    const target = hinted ?? (hostContext.kind === "root" && !isPending ? sessionTenantSlug : null);
    if (!target || target === hostSlug || getTenantSlugHostError(target) !== null) return null;
    return target;
  }, [hostContext, hostSlug, hints, isPending, sessionTenantSlug]);

  useEffect(() => {
    if (hostRedirectSlug) {
      // The sso hint only matters on the target host's login page
      const url = new URL(cleanedUrl, window.location.origin);
      const sso = hints.get("sso");
      if (sso !== null) url.searchParams.set("sso", sso);
      window.location.replace(tenantUrl(hostRedirectSlug, url.pathname + url.search + url.hash));
      return;
    }
    if (isPending) return;

    if (session) {
      // QL-343 — fail closed: the session must name a tenant that has a host.
      // On a tenant host, or on the root domain where there is nowhere to send
      // it, sign out through /clear (keeping the page) so the next sign-in
      // carries a usable tenant instead of leaving the user on the loader.
      const hasUsableTenant =
        sessionTenantSlug !== null && getTenantSlugHostError(sessionTenantSlug) === null;
      if ((hostSlug && !sessionTenantSlug) || (hostContext.kind === "root" && !hasUsableTenant)) {
        window.location.replace(
          cleanedUrl === "/" ? "/clear" : `/clear?redirect=${encodeURIComponent(cleanedUrl)}`,
        );
        return;
      }
      if (hints.size > 0) navigate(cleanedUrl, { replace: true });
      return;
    }

    const loginParams = new URLSearchParams(hints);
    if (cleanedUrl !== "/") loginParams.set("redirect", cleanedUrl);
    navigate(loginParams.size > 0 ? `/login?${loginParams.toString()}` : "/login", {
      replace: true,
    });
  }, [
    hostRedirectSlug,
    hostContext.kind,
    isPending,
    session,
    hostSlug,
    sessionTenantSlug,
    hints,
    cleanedUrl,
    navigate,
  ]);

  if (hostRedirectSlug) return <FullScreenLoader />;
  if (isPending) return <FullScreenLoader />;
  if (!session) return null;
  // The root domain never renders the app itself; a session without a usable
  // tenant slug stays on the loader rather than showing the wrong organization
  if (hostContext.kind === "root") return <FullScreenLoader />;
  // Fail closed on a tenant host: a session that cannot say which organization
  // it belongs to is signed out (the effect above) instead of rendering the app
  if (hostSlug && !sessionTenantSlug) return <FullScreenLoader />;

  if (hostSlug && sessionTenantSlug && sessionTenantSlug !== hostSlug) {
    return (
      <WrongTenantOverlay
        sessionTenantSlug={sessionTenantSlug}
        hostTenantSlug={hostSlug}
        returnTo={cleanedUrl}
      />
    );
  }

  return <Outlet />;
}
